import { useEffect, useId, useState, type ReactNode } from "react";
import { Check, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * Building blocks of the step-by-step assistant that creates a prompt tool
 * (`PromptToolManagement`): choices as cards and optional parts behind a fold.
 */

interface DisclosureProps {
    title: string;
    description?: string;
    /** Controlled open state; without it the part keeps its own. */
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    children: ReactNode;
}

/**
 * A fold for optional or advanced parts. Its content is mounted the first time it opens and then
 * **stays mounted** (just hidden): editors inside it keep their state when it is folded again.
 */
export function Disclosure({ title, description, open: controlledOpen, onOpenChange, children }: DisclosureProps) {
    const id = useId();
    const [ownOpen, setOwnOpen] = useState(false);
    const open = controlledOpen ?? ownOpen;
    const [everOpened, setEverOpened] = useState(open);
    useEffect(() => {
        if (open) setEverOpened(true);
    }, [open]);

    const toggle = () => {
        setOwnOpen(!open);
        onOpenChange?.(!open);
    };

    return (
        <div className="rounded-xl border bg-card">
            <button
                type="button"
                aria-expanded={open}
                aria-controls={id}
                onClick={toggle}
                className="flex w-full items-start gap-3 rounded-xl p-4 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
                <ChevronRight
                    aria-hidden
                    className={cn("mt-0.5 size-4 shrink-0 text-muted-foreground transition-transform duration-200", open && "rotate-90")}
                />
                <span className="min-w-0">
                    <span className="block text-sm font-medium">{title}</span>
                    {description ? <span className="mt-0.5 block text-sm text-muted-foreground">{description}</span> : null}
                </span>
            </button>
            <div
                id={id}
                className={cn(
                    "grid transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none",
                    open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                )}
            >
                <div className={cn("min-h-0 overflow-hidden", !open && "invisible")}>
                    {everOpened ? <div className="border-t p-4">{children}</div> : null}
                </div>
            </div>
        </div>
    );
}

export interface ChoiceOption<T extends string> {
    value: T;
    title: string;
    description: string;
    icon: ReactNode;
    /** A small hint on the card, e.g. "Empfohlen". */
    badge?: string;
}

/** A single choice among a few, as compact cards with a plain-language description. */
export function ChoiceCards<T extends string>({
    name,
    label,
    value,
    options,
    onChange,
    hideLabel = false,
}: {
    name: string;
    label: string;
    /** The step title already asks the question: keep the legend for screen readers only. */
    hideLabel?: boolean;
    value: T | undefined;
    options: ChoiceOption<T>[];
    onChange: (value: T) => void;
}) {
    return (
        <fieldset className="min-w-0">
            <legend className={cn("text-sm font-medium", hideLabel ? "sr-only" : "mb-2")}>{label}</legend>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {options.map((option) => (
                    <label key={option.value} className="relative block cursor-pointer">
                        <input
                            type="radio"
                            name={name}
                            value={option.value}
                            checked={value === option.value}
                            onChange={() => onChange(option.value)}
                            className="peer sr-only"
                        />
                        <span
                            className={cn(
                                "flex h-full flex-col gap-4 rounded-xl border bg-background p-5 transition-all duration-150",
                                "hover:border-foreground/30 hover:bg-muted/40 peer-focus-visible:ring-2 peer-focus-visible:ring-ring",
                                "peer-checked:border-primary peer-checked:bg-primary/5 peer-checked:ring-1 peer-checked:ring-primary",
                                // The tick shows the choice without relying on colour alone.
                                "peer-checked:[&_[data-tick]]:scale-100 peer-checked:[&_[data-tick]]:opacity-100",
                            )}
                        >
                            <span className="flex items-start justify-between gap-3">
                                <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-muted [&>svg]:size-6">
                                    {option.icon}
                                </span>
                                <span
                                    data-tick
                                    aria-hidden
                                    className="grid size-6 scale-75 place-items-center rounded-full bg-primary text-primary-foreground opacity-0 transition-all duration-150"
                                >
                                    <Check className="size-3.5" />
                                </span>
                            </span>
                            <span className="flex flex-col gap-1.5">
                                <span className="flex flex-wrap items-center gap-2">
                                    <span className="text-base font-medium">{option.title}</span>
                                    {option.badge ? (
                                        <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
                                            {option.badge}
                                        </span>
                                    ) : null}
                                </span>
                                <span className="text-sm leading-relaxed text-muted-foreground">{option.description}</span>
                            </span>
                        </span>
                    </label>
                ))}
            </div>
        </fieldset>
    );
}

/** Main column plus a side column (preview, tips) that stays in view while the main column scrolls. */
export function StepLayout({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
    if (!aside) return <div className="flex min-w-0 flex-col gap-8">{children}</div>;
    return (
        <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1fr)_19rem]">
            <div className="flex min-w-0 flex-col gap-8">{children}</div>
            <aside className="min-w-0 lg:sticky lg:top-4 lg:h-fit">{aside}</aside>
        </div>
    );
}

/** A quiet card for hints next to a step. */
export function SideCard({ title, children }: { title: string; children: ReactNode }) {
    return (
        <div className="flex flex-col gap-3 rounded-xl border bg-muted/30 p-4">
            <div className="text-sm font-medium">{title}</div>
            {children}
        </div>
    );
}
